import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { HOME, NO_PERMISSION, SIGN_IN } from 'routes'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import { PROJECT } from 'app-constants'
import { setRedirectTo } from 'helpers/redirect'
import { safeLocalStorage } from 'helpers/localStorage'

const NoPermission: React.FC<{ inProcess?: boolean; type?: string; id?: string; project?: string }> = ({
  inProcess,
  type,
  id,
  project
}) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const permissionType = type === undefined ? searchParams.get('type') : type
  const permissionId = id === undefined ? searchParams.get('id') : id
  const permissionProject = project === undefined ? searchParams.get('hub') : project
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [requestedPermission, setRequestedPermission] = useState<boolean>(false)

  useEffect(() => {
    if (!user) {
      // User is not authenticated — likely a session timeout.
      // Only save a redirect target if one isn't already set (e.g. by the project provider).
      const existingRedirect = safeLocalStorage.getItem('redirectTo')
      if (!existingRedirect) {
        const currentPath = window.location.pathname + window.location.search
        if (!currentPath.startsWith(NO_PERMISSION)) {
          // Inline rendering (e.g. inside a spreadsheet) — current URL is the original page
          setRedirectTo(currentPath)
        } else if (permissionType && permissionId && permissionProject) {
          // Navigated to /nopermission — reconstruct the original URL from search params
          if (permissionType === 'hub' || permissionType === 'project') {
            setRedirectTo(`/hub/${permissionProject}`)
          } else if (permissionType === 'document' || permissionType === 'process') {
            setRedirectTo(`/hub/${permissionProject}/document/${permissionId}`)
          }
          // For 'view' type we cannot fully reconstruct (missing table ID),
          // so we fall back to the hub page
          else if (permissionType === 'view') {
            setRedirectTo(`/hub/${permissionProject}`)
          }
        }
      }

      navigate(SIGN_IN, { replace: true })
    }
  }, [user, navigate, permissionType, permissionId, permissionProject])

  const requestPermission = () => {
    if (permissionType && permissionId && permissionProject) {
      setRequestedPermission(true)
      api
        .requestPermission(permissionProject, permissionType, permissionId)
        .then(() => {
          setSnackbarMessage({
            status: 'success',
            message: `Permission has been requested from the ${PROJECT} builders.`
          })
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  if (!user) return null

  return (
    <div className="bg-surface flex flex-1 items-center justify-center overflow-hidden box-border h-screen w-full px-20">
      <div className="flex flex-col w-full justify-center items-center">
        <div className={inProcess ? 'text-2xl mt-[200px]' : 'text-[2rem] mt-0'}>
          You do not have permission to view that content.
        </div>
        <div className="flex mt-2">
          {permissionType && permissionId && permissionProject && !requestedPermission && (
            <Button className="mr-5" internalType="secondary" onClick={() => requestPermission()}>
              Request Permission
            </Button>
          )}
          {!inProcess && <Button to={HOME}>Return To Home</Button>}
        </div>
      </div>
    </div>
  )
}

export default NoPermission
